// =======================================================
// NeuralMesh — a thinking neural network with visible cascades
// =======================================================
//
// What you see, in order:
//   1. A neuron fires → it brightens and emits an expanding ring shock.
//   2. Comet-shaped pulses (bright head + fading tail) travel outward
//      along every connected synapse at synaptic-delay speed.
//   3. When a pulse arrives at the far neuron, that neuron fires too.
//   4. Repeat. Spontaneous firings periodically seed fresh activity.
//
// Implementation:
//   * Neurons & synapses laid out via jittered grid + k-NN with a
//     distance cap so edges read as a clean connectome (no skylines).
//   * Pulses are discrete events { edgeIdx, fromIdx, startTime }.
//     Travel time = edgeLength / speed → longer synapses delay more,
//     which gives the cascades natural rhythm.
//   * Edges are LineSegments with vertex colors; per frame we clear
//     to a dim base then additively paint each live pulse as a comet.
//   * Ripples are a separate Points mesh with a ring shader (expanding
//     hollow circle, fading with age).
//   * Refractory period prevents a neuron re-firing instantly, so
//     cascades unfold step by step instead of detonating all at once.

import * as THREE from 'three';
import { Generator } from './Generator.js';

// -------------------- shaders --------------------

const NODE_VERT = /* glsl */ `
  attribute float aActive;
  uniform float uPxScale;
  uniform float uBaseSize;
  varying float vActive;
  void main() {
    vActive = aActive;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uBaseSize * uPxScale * (1.0 + aActive * 2.4);
  }
`;
const NODE_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uHotColor;
  varying float vActive;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c);
    if (r > 0.5) discard;
    float core = smoothstep(0.5, 0.0, r);
    float halo = smoothstep(0.5, 0.15, r);
    float a = clamp(vActive, 0.0, 1.5);
    // Square the visual response so activation decay drops out of the
    // trail-buffer accumulation budget quickly. A linear tail at 30%
    // would leave a long grey ghost; squared, it's already at 9%.
    float aVis = a * a;
    float rest = 0.08 * smoothstep(0.05, 0.15, a);
    vec3 col = mix(uColor, uHotColor, clamp(a, 0.0, 1.0));
    float intensity = core * (rest + aVis * 1.7) + halo * aVis * 0.25;
    if (intensity < 0.05) discard;
    gl_FragColor = vec4(col * intensity, clamp(intensity, 0.0, 1.0));
  }
`;

const RIPPLE_VERT = /* glsl */ `
  attribute float aAge;       // seconds since spawn (negative = inactive)
  uniform float uPxScale;
  uniform float uMaxRadiusPx; // ring radius at age=lifetime, in pixels
  uniform float uLifetime;
  varying float vAge;
  varying float vAlpha;
  void main() {
    vAge = aAge;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float k = clamp(aAge / uLifetime, 0.0, 1.0);
    // Point size = ring diameter; we draw the ring inside the sprite.
    float sizePx = uMaxRadiusPx * 2.0 * (0.2 + k);
    gl_PointSize = sizePx * uPxScale;
    vAlpha = (aAge >= 0.0) ? (1.0 - k) : 0.0;
  }
`;
const RIPPLE_FRAG = /* glsl */ `
  uniform vec3 uColor;
  varying float vAlpha;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c) * 2.0;            // 0..1 across sprite
    if (r > 1.0 || vAlpha <= 0.0) discard;
    // hollow ring: bright at r ≈ 0.85, falls off both sides
    float ring = exp(-pow((r - 0.85) / 0.12, 2.0));
    float a = ring * vAlpha;
    gl_FragColor = vec4(uColor * a * 1.4, a);
  }
`;

// -------------------- generator --------------------

const EDGE_SUBDIV   = 18;     // segments per edge
const NODE_K        = 3;      // nearest-neighbour count for topology
const MAX_PULSES    = 800;
const MAX_RIPPLES   = 96;
const REFRACTORY_S  = 0.18;
const RIPPLE_LIFE   = 0.8;

export class NeuralMesh extends Generator {
  constructor() {
    super({
      name: 'Neural Mesh',
      category: 'intense',
      schema: {
        nodes:        { default: 110, min: 30,  max: 1200, step: 1,   label: 'Neurons' },
        speed:        { default: 1.6, min: 0.3, max: 5,   step: 0.05, label: 'Pulse speed' },
        spontaneous:  { default: 1.4, min: 0,   max: 8,   step: 0.05, label: 'Spontaneous fires/s' },
        decay:        { default: 2.6, min: 0.5, max: 8,   step: 0.05, label: 'Fire decay' },
        cascadeDecay: { default: 0.65, min: 0,  max: 1,   step: 0.01, label: 'Cascade survival' },
        cascadeMin:   { default: 0.08, min: 0.01, max: 0.5, step: 0.01, label: 'Cascade cutoff' },
        lineWidth:    { default: 0.0025, min: 0.0005, max: 0.02, step: 0.0005, label: 'Synapse width' },
        pulseHead:    { default: 0.05, min: 0.005, max: 0.4, step: 0.005, label: 'Pulse head width' },
        pulseTail:    { default: 0.55, min: 0.02,  max: 1.0, step: 0.01,  label: 'Pulse tail length' },
        nodeSize:     { default: 6.0, min: 1,   max: 16,  step: 0.1,  label: 'Node size (px)' },
        rippleSize:   { default: 80,  min: 10,  max: 240, step: 1,    label: 'Ripple radius (px)' },
        edgeColor:    { default: '#13313a', type: 'color', label: 'Synapse rest' },
        pulseColor:   { default: '#7afff0', type: 'color', label: 'Pulse' },
        nodeColor:    { default: '#0e6a55', type: 'color', label: 'Neuron rest' },
        firedColor:   { default: '#ffffff', type: 'color', label: 'Neuron fired' },
        rippleColor:  { default: '#7afff0', type: 'color', label: 'Ripple' },
      }
    });
    this._needsRebuild = false;
    this._spontAccum = 0;
  }

  // -----------------------------------------
  // Topology
  // -----------------------------------------
  _generateTopology(engine) {
    const aspect = engine.width / engine.height;
    const N = Math.max(8, Math.floor(this.v('nodes')));

    const cols = Math.max(2, Math.round(Math.sqrt(N * aspect)));
    const rows = Math.max(2, Math.ceil(N / cols));
    // Camera space is [-aspect, aspect] x [-1, 1] — fill it edge-to-edge.
    const xSpan = aspect * 2;
    const ySpan = 2;
    const cellX = xSpan / cols;
    const cellY = ySpan / rows;

    this._nodes = [];
    let i = 0;
    for (let r = 0; r < rows && i < N; r++) {
      for (let c = 0; c < cols && i < N; c++) {
        const u = (c + 0.5) / cols - 0.5;
        const w = (r + 0.5) / rows - 0.5;
        const jx = (this.rand01(i * 2 + 1)   - 0.5) * cellX * 0.55;
        const jy = (this.rand01(i * 2 + 100) - 0.5) * cellY * 0.55;
        this._nodes.push({
          x: u * xSpan + jx,
          y: w * ySpan + jy,
          activation: 0,
          lastFire: -10,
          neighbors: [],
        });
        i++;
      }
    }

    // distance-cap k-NN
    const distCap = Math.hypot(cellX, cellY) * 1.9;
    const cap2 = distCap * distCap;
    const seen = new Set();
    this._edges = [];
    for (let a = 0; a < this._nodes.length; a++) {
      const na = this._nodes[a];
      const cand = [];
      for (let b = 0; b < this._nodes.length; b++) {
        if (b === a) continue;
        const nb = this._nodes[b];
        const dx = nb.x - na.x, dy = nb.y - na.y;
        const d2 = dx*dx + dy*dy;
        if (d2 > cap2) continue;
        cand.push({ b, d: d2 });
      }
      cand.sort((p, q) => p.d - q.d);
      for (let kk = 0; kk < NODE_K && kk < cand.length; kk++) {
        const b = cand[kk].b;
        const lo = Math.min(a, b), hi = Math.max(a, b);
        const key = lo * 100000 + hi;
        if (seen.has(key)) continue;
        seen.add(key);
        const eIdx = this._edges.length;
        const dx = this._nodes[hi].x - this._nodes[lo].x;
        const dy = this._nodes[hi].y - this._nodes[lo].y;
        const len = Math.hypot(dx, dy);
        this._edges.push({ a: lo, b: hi, length: len });
        this._nodes[lo].neighbors.push({ edgeIdx: eIdx, other: hi });
        this._nodes[hi].neighbors.push({ edgeIdx: eIdx, other: lo });
      }
    }

    this._pulses = [];
    this._ripples = [];
  }

  _buildMeshes(scene) {
    // Edges as a quad-strip mesh so synapse width is actually controllable
    // (LineBasicMaterial.linewidth is ignored on WebGL).
    // Per edge: (segs+1) ring vertices, each duplicated as a left/right pair.
    const segs = EDGE_SUBDIV;
    const E = this._edges.length;
    const ringCount = segs + 1;
    const vertsPerEdge = ringCount * 2;
    const trisPerEdge = segs * 2;
    const totalVerts = E * vertsPerEdge;
    const totalIndices = E * trisPerEdge * 3;

    const positions = new Float32Array(totalVerts * 3);
    const colors    = new Float32Array(totalVerts * 3);
    const indices   = new Uint32Array(totalIndices);
    this._edgeT     = new Float32Array(totalVerts);
    this._edgeVertsPerEdge = vertsPerEdge;

    const w = this.v('lineWidth');
    for (let e = 0; e < E; e++) {
      const ed = this._edges[e];
      const a = this._nodes[ed.a], b = this._nodes[ed.b];
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const px = (-dy / len) * w;
      const py = ( dx / len) * w;
      const baseV = e * vertsPerEdge;
      for (let s = 0; s < ringCount; s++) {
        const t = s / segs;
        const cx = a.x + dx * t;
        const cy = a.y + dy * t;
        const li = baseV + s * 2;
        const ri = li + 1;
        positions[li*3+0] = cx + px;
        positions[li*3+1] = cy + py;
        positions[ri*3+0] = cx - px;
        positions[ri*3+1] = cy - py;
        this._edgeT[li] = t;
        this._edgeT[ri] = t;
      }
      const baseI = e * trisPerEdge * 3;
      for (let s = 0; s < segs; s++) {
        const l0 = baseV + s * 2;
        const r0 = l0 + 1;
        const l1 = l0 + 2;
        const r1 = l0 + 3;
        const ii = baseI + s * 6;
        indices[ii+0] = l0; indices[ii+1] = r0; indices[ii+2] = l1;
        indices[ii+3] = r0; indices[ii+4] = r1; indices[ii+5] = l1;
      }
    }

    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    edgeGeo.setAttribute('color',    new THREE.BufferAttribute(colors, 3));
    edgeGeo.setIndex(new THREE.BufferAttribute(indices, 1));
    const edgeMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this._edgeMesh = new THREE.Mesh(edgeGeo, edgeMat);
    this._edgeMesh.frustumCulled = false;
    this._edgeMesh.renderOrder = 1;
    scene.add(this._edgeMesh);
    this.track(this._edgeMesh);

    // Nodes
    const N = this._nodes.length;
    const npos = new Float32Array(N * 3);
    const nact = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      npos[i*3+0] = this._nodes[i].x;
      npos[i*3+1] = this._nodes[i].y;
    }
    const nodeGeo = new THREE.BufferGeometry();
    nodeGeo.setAttribute('position', new THREE.BufferAttribute(npos, 3));
    nodeGeo.setAttribute('aActive',  new THREE.BufferAttribute(nact, 1));
    const nodeMat = new THREE.ShaderMaterial({
      vertexShader: NODE_VERT, fragmentShader: NODE_FRAG,
      uniforms: {
        uColor:    { value: new THREE.Color(this.v('nodeColor')) },
        uHotColor: { value: new THREE.Color(this.v('firedColor')) },
        uBaseSize: { value: this.v('nodeSize') },
        uPxScale:  { value: this._pxScale ?? 1 },
      },
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this._nodeMesh = new THREE.Points(nodeGeo, nodeMat);
    this._nodeMesh.frustumCulled = false;
    this._nodeMesh.renderOrder = 3;
    scene.add(this._nodeMesh);
    this.track(this._nodeMesh);

    // Ripples (fixed-capacity ring buffer)
    const rpos = new Float32Array(MAX_RIPPLES * 3);
    const rage = new Float32Array(MAX_RIPPLES);
    rage.fill(-1);
    this._rippleSlot = 0;
    const rgeo = new THREE.BufferGeometry();
    rgeo.setAttribute('position', new THREE.BufferAttribute(rpos, 3));
    rgeo.setAttribute('aAge',     new THREE.BufferAttribute(rage, 1));
    rgeo.setDrawRange(0, MAX_RIPPLES);
    const rmat = new THREE.ShaderMaterial({
      vertexShader: RIPPLE_VERT, fragmentShader: RIPPLE_FRAG,
      uniforms: {
        uColor:       { value: new THREE.Color(this.v('rippleColor')) },
        uMaxRadiusPx: { value: this.v('rippleSize') },
        uLifetime:    { value: RIPPLE_LIFE },
        uPxScale:     { value: this._pxScale ?? 1 },
      },
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this._rippleMesh = new THREE.Points(rgeo, rmat);
    this._rippleMesh.frustumCulled = false;
    this._rippleMesh.renderOrder = 2;
    scene.add(this._rippleMesh);
    this.track(this._rippleMesh);
  }

  _rebuild(scene, engine) {
    if (this._edgeMesh) {
      scene.remove(this._edgeMesh);
      this._edgeMesh.geometry.dispose();
      this._edgeMesh.material.dispose();
    }
    if (this._nodeMesh) {
      scene.remove(this._nodeMesh);
      this._nodeMesh.geometry.dispose();
      this._nodeMesh.material.dispose();
    }
    if (this._rippleMesh) {
      scene.remove(this._rippleMesh);
      this._rippleMesh.geometry.dispose();
      this._rippleMesh.material.dispose();
    }
    this._disposables = [];
    this._generateTopology(engine);
    this._buildMeshes(scene);
  }

  // -----------------------------------------
  // Lifecycle
  // -----------------------------------------
  onEnter(scene, engine) {
    this.scene = scene;
    this.engine = engine;
    this._pxScale = Math.max(1, engine.height / 1080);
    this._rebuild(scene, engine);
    // Seed a couple of initial firings so something is happening immediately.
    for (let i = 0; i < 3; i++) {
      const idx = Math.floor(this.rand01(50 + i) * this._nodes.length);
      this._fireNode(idx, 0);
    }
  }

  reseed() {
    this.seed = (Math.random() * 0xffffffff) >>> 0;
    this._needsRebuild = true;
  }

  onResize(w, h) {
    this._pxScale = Math.max(1, h / 1080);
    if (this._nodeMesh)   this._nodeMesh.material.uniforms.uPxScale.value = this._pxScale;
    if (this._rippleMesh) this._rippleMesh.material.uniforms.uPxScale.value = this._pxScale;
  }

  onParamChange(key, val) {
    if (this._nodeMesh) {
      const u = this._nodeMesh.material.uniforms;
      if (key === 'nodeColor')  u.uColor.value.set(val);
      if (key === 'firedColor') u.uHotColor.value.set(val);
      if (key === 'nodeSize')   u.uBaseSize.value = val;
    }
    if (this._rippleMesh) {
      const u = this._rippleMesh.material.uniforms;
      if (key === 'rippleColor') u.uColor.value.set(val);
      if (key === 'rippleSize')  u.uMaxRadiusPx.value = val;
    }
    if (key === 'nodes' || key === 'lineWidth') this._needsRebuild = true;
  }

  // -----------------------------------------
  // Sim helpers
  // -----------------------------------------
  _fireNode(i, time, strength = 1.0) {
    const n = this._nodes[i];
    if (time - n.lastFire < REFRACTORY_S) return;
    if (strength < this.v('cascadeMin')) return;
    n.lastFire = time;
    n.activation = Math.min(1.6, 0.4 + strength);
    this._spawnRipple(n.x, n.y, time, strength);
    // Outgoing pulses inherit a decayed strength → cascades fade out.
    const childStrength = strength * this.v('cascadeDecay');
    if (childStrength < this.v('cascadeMin')) return;
    if (this._pulses.length + n.neighbors.length > MAX_PULSES) return;
    for (const nb of n.neighbors) {
      // Don't bounce back along the synapse we just arrived on.
      if (nb.other === n.lastFireSrc) continue;
      this._pulses.push({
        edgeIdx: nb.edgeIdx,
        fromIdx: i,
        toIdx:   nb.other,
        startTime: time,
        strength: childStrength,
      });
    }
  }

  _spawnRipple(x, y, time) {
    if (!this._rippleMesh) return;
    const slot = this._rippleSlot;
    this._rippleSlot = (slot + 1) % MAX_RIPPLES;
    const pos = this._rippleMesh.geometry.attributes.position.array;
    const age = this._rippleMesh.geometry.attributes.aAge.array;
    pos[slot*3+0] = x;
    pos[slot*3+1] = y;
    pos[slot*3+2] = 0;
    age[slot] = 0;
    this._rippleMesh.geometry.attributes.position.needsUpdate = true;
    this._rippleMesh.geometry.attributes.aAge.needsUpdate = true;
    // Stamp spawn time so we can compute fresh age each frame.
    if (!this._rippleSpawn) this._rippleSpawn = new Float32Array(MAX_RIPPLES).fill(-1);
    this._rippleSpawn[slot] = time;
  }

  // -----------------------------------------
  // Per-frame
  // -----------------------------------------
  update(dt, time, engine) {
    if (this._needsRebuild) {
      this._needsRebuild = false;
      this._rebuild(this.scene, engine);
    }
    if (!this._edgeMesh) return;

    const speed       = this.v('speed');
    const spontRate   = this.v('spontaneous');
    const decayRate   = this.v('decay');
    const edgeColor   = new THREE.Color(this.v('edgeColor'));
    const pulseColor  = new THREE.Color(this.v('pulseColor'));

    // 1. Spontaneous ignitions keep the network alive.
    this._spontAccum += dt * spontRate;
    while (this._spontAccum >= 1) {
      this._spontAccum -= 1;
      const i = Math.floor(Math.random() * this._nodes.length);
      this._fireNode(i, time);
    }

    // 2. Advance pulses; on arrival, fire receiver.
    const live = [];
    for (const p of this._pulses) {
      const ed = this._edges[p.edgeIdx];
      const travel = ed.length / Math.max(0.001, speed);
      const pos = (time - p.startTime) / travel;
      if (pos >= 1) {
        // Mark inbound source so receiver doesn't ricochet straight back.
        this._nodes[p.toIdx].lastFireSrc = p.fromIdx;
        this._fireNode(p.toIdx, time, p.strength);
        this._nodes[p.toIdx].lastFireSrc = -1;
      } else if (pos >= 0) {
        live.push(p);
      }
    }
    this._pulses = live;

    // 3. Decay node activations.
    const decay = Math.exp(-dt * decayRate);
    for (let i = 0; i < this._nodes.length; i++) {
      this._nodes[i].activation *= decay;
    }

    // 4. Repaint edges: dim base, then additive comet for each pulse.
    const colors = this._edgeMesh.geometry.attributes.color.array;
    const tArr   = this._edgeT;
    const vertsPerEdge = this._edgeVertsPerEdge;
    const headSigma = this.v('pulseHead');
    const tailLen   = this.v('pulseTail');

    // Dim base fill.
    const baseR = edgeColor.r * 0.9;
    const baseG = edgeColor.g * 0.9;
    const baseB = edgeColor.b * 0.9;
    for (let v = 0; v < colors.length; v += 3) {
      colors[v + 0] = baseR;
      colors[v + 1] = baseG;
      colors[v + 2] = baseB;
    }

    // Comet paint: bright head + tail trailing behind the direction of travel.
    const dPulseR = pulseColor.r;
    const dPulseG = pulseColor.g;
    const dPulseB = pulseColor.b;
    const sigma2 = headSigma * headSigma;
    for (const p of this._pulses) {
      const ed = this._edges[p.edgeIdx];
      const travel = ed.length / Math.max(0.001, speed);
      const pos = (time - p.startTime) / travel;     // 0..1
      if (pos < 0 || pos > 1) continue;
      const flip = (p.fromIdx !== ed.a);              // pulse goes b → a
      const base = p.edgeIdx * vertsPerEdge;
      for (let v = 0; v < vertsPerEdge; v++) {
        const idx = base + v;
        const tNorm = flip ? (1 - tArr[idx]) : tArr[idx];
        const d = pos - tNorm;                        // positive = vertex behind head
        if (d < -headSigma * 2 || d > tailLen) continue;
        const head = Math.exp(-(d * d) / sigma2);
        const tailFrac = Math.max(0, d) / tailLen;
        const tail = (1 - tailFrac) * (1 - tailFrac); // soft trailing fade
        const bright = Math.min(2.0, (head * 1.4 + tail * 0.7) * p.strength);
        colors[idx*3+0] += dPulseR * bright;
        colors[idx*3+1] += dPulseG * bright;
        colors[idx*3+2] += dPulseB * bright;
      }
    }
    this._edgeMesh.geometry.attributes.color.needsUpdate = true;

    // 5. Push activations to GPU.
    const nact = this._nodeMesh.geometry.attributes.aActive.array;
    for (let i = 0; i < this._nodes.length; i++) {
      // No constant ambient — trails would compound it into a grey halo.
      // Nodes are only visible while activation > 0.
      nact[i] = Math.min(1.6, this._nodes[i].activation);
    }
    this._nodeMesh.geometry.attributes.aActive.needsUpdate = true;

    // 6. Age ripples.
    if (this._rippleSpawn) {
      const age = this._rippleMesh.geometry.attributes.aAge.array;
      for (let i = 0; i < MAX_RIPPLES; i++) {
        const s = this._rippleSpawn[i];
        if (s < 0) { age[i] = -1; continue; }
        const a = time - s;
        age[i] = (a < RIPPLE_LIFE) ? a : -1;
      }
      this._rippleMesh.geometry.attributes.aAge.needsUpdate = true;
    }
  }
}
